import React, { useState } from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import ImageModal from './ImageModal';

const CarouselSection = styled.section`
  width: 100%;
  padding: ${props => props.theme.spacing.mega} 0;
  background: ${props => props.theme.colors.background.secondary};
`;

const CarouselContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.large};
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: ${props => props.theme.fontSizes.hero};
  margin-bottom: ${props => props.theme.spacing.xxlarge};

  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.neon.cyan} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SliderWrapper = styled.div`
  .slick-slide {
    padding: 0 ${props => props.theme.spacing.medium};
  }

  .slick-dots {
    bottom: -40px;

    li button:before {
      color: ${props => props.theme.colors.primary};
      font-size: 12px;
    }

    li.slick-active button:before {
      color: ${props => props.theme.colors.primary};
    }
  }

  .slick-arrow {
    z-index: 10;

    &:before {
      color: ${props => props.theme.colors.primary};
      font-size: 30px;
    }
  }

  .slick-prev {
    left: -40px;

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      left: 10px;
    }
  }

  .slick-next {
    right: -40px;

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      right: 10px;
    }
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  cursor: pointer;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.large};

  &:hover {
    .zoom-icon {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }

    img {
      transform: scale(1.05);
    }
  }
`;

const SlideImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.large};
  transition: transform ${props => props.theme.transitions.medium};
  display: block;
`;

const ZoomIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  width: 60px;
  height: 60px;
  background: ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.round};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all ${props => props.theme.transitions.medium};
  box-shadow: ${props => props.theme.shadows.glowStrong};
  z-index: 5;

  &::before {
    content: '🔍';
    font-size: 2rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
    width: 50px;
    height: 50px;

    &::before {
      font-size: 1.5rem;
    }
  }
`;

interface CarouselImage {
  src: string;
  alt: string;
}

const ImageCarousel: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<CarouselImage | null>(null);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  const carouselImages: CarouselImage[] = [
    {
      src: '/assets/images/app-1-script-library.jpg',
      alt: 'Script Library - Manage your prompt library'
    },
    {
      src: '/assets/images/app-2-saving-prompts.jpg',
      alt: 'Saving Prompts - Save and organize'
    },
    {
      src: '/assets/images/app-3-parameters-values.jpg',
      alt: 'Parameters Values - Custom values'
    },
    {
      src: '/assets/images/app-4-parameters-builder.jpg',
      alt: 'Parameters Builder - Build parameters'
    },
    {
      src: '/assets/images/app-5-main-menu.jpg',
      alt: 'Main Menu - NeuralPrompt interface'
    },
    {
      src: '/assets/images/app-6-copy-prompt.jpg',
      alt: 'Copy Prompt - Execute your prompts'
    },
    {
      src: '/assets/images/app-7-attributes-selection.jpg',
      alt: 'Attributes Selection - Choose attributes'
    },
    {
      src: '/assets/images/app-8-about-neuralprompt.jpg',
      alt: 'About NeuralPrompt - Application info'
    }
  ];

  const handleImageClick = (image: CarouselImage) => {
    setSelectedImage(image);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
  };

  return (
    <CarouselSection id="experience">
      <CarouselContainer>
        <SectionTitle>Experience NeuralPrompt Desktop</SectionTitle>
        <SliderWrapper>
          <Slider {...settings}>
            {carouselImages.map((image, index) => (
              <div key={index}>
                <ImageWrapper onClick={() => handleImageClick(image)}>
                  <SlideImage src={image.src} alt={image.alt} />
                  <ZoomIcon className="zoom-icon" />
                </ImageWrapper>
              </div>
            ))}
          </Slider>
        </SliderWrapper>
      </CarouselContainer>

      {selectedImage && (
        <ImageModal
          isOpen={modalOpen}
          imageSrc={selectedImage.src}
          imageAlt={selectedImage.alt}
          onClose={handleCloseModal}
        />
      )}
    </CarouselSection>
  );
};

export default ImageCarousel;
